import { useCallback } from 'react'
import type { OrderEventActionFormValues } from '@klear/order-event/event-form'
import { orderEventActionFormValuesToJson } from '@klear/order-event/wire.client'
import {
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_PURCHASE_ORDER,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PAYMENT
} from '@klear/constants'
import { trpc, type RouterInputs } from '@klear/ui/lib/trpc'
import type { OrderEventRef } from '@klear/ui/hooks/order-event-ref'
import { useOrderEventInvalidate } from '@klear/ui/hooks/use-order-event-invalidate'

type ActionInput = RouterInputs['invoice']['action']
type DeleteInput = RouterInputs['invoice']['delete']

function toActionInput(ref: OrderEventRef, actionSchema: OrderEventActionFormValues): ActionInput {
  return {
    id: ref.id,
    orderId: ref.orderId,
    traderId: ref.traderId,
    actionJson: orderEventActionFormValuesToJson(actionSchema)
  }
}

function toDeleteInput(ref: OrderEventRef): DeleteInput {
  return { id: ref.id, orderId: ref.orderId, traderId: ref.traderId }
}

function useDomainActionDelete(
  actionMutation: { mutateAsync: (input: ActionInput) => Promise<unknown> },
  deleteMutation: { mutateAsync: (input: DeleteInput) => Promise<unknown> }
) {
  const invalidate = useOrderEventInvalidate()

  const performAction = useCallback(
    async (ref: OrderEventRef, actionSchema: OrderEventActionFormValues) => {
      await actionMutation.mutateAsync(toActionInput(ref, actionSchema))
      await invalidate()
    },
    [actionMutation.mutateAsync, invalidate]
  )

  const deleteEvent = useCallback(
    async (ref: OrderEventRef) => {
      await deleteMutation.mutateAsync(toDeleteInput(ref))
      await invalidate()
    },
    [deleteMutation.mutateAsync, invalidate]
  )

  return { performAction, deleteEvent }
}

export function useInvoiceMutations() {
  const invalidate = useOrderEventInvalidate()
  const action = trpc.invoice.action.useMutation()
  const del = trpc.invoice.delete.useMutation()
  const preview = trpc.invoice.preview.useMutation()
  const commit = trpc.invoice.commit.useMutation()
  const { performAction, deleteEvent } = useDomainActionDelete(action, del)

  const commitCommit = useCallback(
    async (input: RouterInputs['invoice']['commit']) => {
      const result = await commit.mutateAsync(input)
      await invalidate()
      return result
    },
    [commit.mutateAsync, invalidate]
  )

  return {
    performAction,
    deleteEvent,
    previewCommit: preview.mutateAsync,
    commitCommit,
    isActionPending: action.isPending,
    isDeletePending: del.isPending,
    isPreviewPending: preview.isPending,
    isCommitPending: commit.isPending
  }
}

export function usePurchaseOrderMutations() {
  const action = trpc.purchaseOrder.action.useMutation()
  const del = trpc.purchaseOrder.delete.useMutation()
  const { performAction, deleteEvent } = useDomainActionDelete(action, del)

  return {
    performAction,
    deleteEvent,
    isActionPending: action.isPending,
    isDeletePending: del.isPending
  }
}

export function useOperationalMutations() {
  const invalidate = useOrderEventInvalidate()
  const del = trpc.operational.delete.useMutation()
  const preview = trpc.operational.preview.useMutation()
  const commit = trpc.operational.commit.useMutation()

  const deleteEvent = useCallback(
    async (ref: OrderEventRef) => {
      await del.mutateAsync(toDeleteInput(ref))
      await invalidate()
    },
    [del.mutateAsync, invalidate]
  )

  return {
    deleteEvent,
    previewCommit: preview.mutateAsync,
    commitCommit: commit.mutateAsync,
    isDeletePending: del.isPending,
    isPreviewPending: preview.isPending,
    isCommitPending: commit.isPending
  }
}

export function useProofOfAcceptanceMutations() {
  const action = trpc.proofOfAcceptance.action.useMutation()
  const del = trpc.proofOfAcceptance.delete.useMutation()
  const preview = trpc.proofOfAcceptance.preview.useMutation()
  const commit = trpc.proofOfAcceptance.commit.useMutation()
  const { performAction, deleteEvent } = useDomainActionDelete(action, del)

  return {
    performAction,
    deleteEvent,
    previewCommit: preview.mutateAsync,
    commitCommit: commit.mutateAsync,
    isActionPending: action.isPending,
    isDeletePending: del.isPending,
    isPreviewPending: preview.isPending,
    isCommitPending: commit.isPending
  }
}

export function useInvoiceOfferMutations() {
  const action = trpc.invoiceOffer.action.useMutation()
  const del = trpc.invoiceOffer.delete.useMutation()
  const { performAction, deleteEvent } = useDomainActionDelete(action, del)

  return {
    performAction,
    deleteEvent,
    isActionPending: action.isPending,
    isDeletePending: del.isPending
  }
}

export function useOrderEventDomainMutations() {
  const invoice = useInvoiceMutations()
  const purchaseOrder = usePurchaseOrderMutations()
  const operational = useOperationalMutations()
  const proofOfAcceptance = useProofOfAcceptanceMutations()
  const invoiceOffer = useInvoiceOfferMutations()

  const performLifecycleAction = useCallback(
    async (ref: OrderEventRef, actionSchema: OrderEventActionFormValues) => {
      switch (ref.type) {
        case PERMISSION_ENTITY_INVOICE:
          return invoice.performAction(ref, actionSchema)
        case PERMISSION_ENTITY_PURCHASE_ORDER:
          return purchaseOrder.performAction(ref, actionSchema)
        case PERMISSION_ENTITY_INVOICE_OFFER:
          return invoiceOffer.performAction(ref, actionSchema)
        case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
          return proofOfAcceptance.performAction(ref, actionSchema)
        case PERMISSION_ENTITY_PAYMENT:
          throw new Error(
            'Timeline payment nodes are not actionable here. Use the payables page or operational payment actions for supplier payables.'
          )
        default:
          throw new Error(`No action endpoint for event type: ${ref.type}`)
      }
    },
    [invoice, purchaseOrder, invoiceOffer, operational, proofOfAcceptance]
  )

  const deleteOrderEvent = useCallback(
    async (ref: OrderEventRef) => {
      switch (ref.type) {
        case PERMISSION_ENTITY_INVOICE:
          return invoice.deleteEvent(ref)
        case PERMISSION_ENTITY_PURCHASE_ORDER:
          return purchaseOrder.deleteEvent(ref)
        case PERMISSION_ENTITY_INVOICE_OFFER:
          return invoiceOffer.deleteEvent(ref)
        case PERMISSION_ENTITY_OPERATIONAL:
          return operational.deleteEvent(ref)
        case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
          return proofOfAcceptance.deleteEvent(ref)
        case PERMISSION_ENTITY_PAYMENT:
          throw new Error(
            'Timeline payment nodes cannot be deleted here. Use the payables page or operational payment actions for supplier payables.'
          )
        default:
          throw new Error(`No delete endpoint for event type: ${ref.type}`)
      }
    },
    [invoice, purchaseOrder, invoiceOffer, operational, proofOfAcceptance]
  )

  return { performLifecycleAction, deleteOrderEvent }
}
